import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Tooltip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import dayjs from 'dayjs';
import { EDITABLE_BORDER } from '../constants';
import * as actions from '../../../../redux/actions';
import { useSpeechDictation } from '../../../../hooks/useSpeechDictation';
import { AiProviderPicker, loadSavedAiProvider, aiPlanLabel } from '../../../../components/AiProviderPicker';
import { AiQuestionsButton } from './AiQuestionsButton';

// กล่องหมายเหตุ — ป้าย chip + กรอบเส้นประสีน้ำเงิน คลิกเพื่อแก้ไข (ข้อมูลหลักของแต่ละ Job)
// หมายเหตุ = log สะสม เพิ่มได้อย่างเดียว (บันทึกเก่าล็อกแก้ไขไม่ได้) — บันทึกใหม่ขึ้นบนสุด
// แต่ละรายการ: "D-M-YY ข้อความ — ชื่อผู้ใช้" คั่นด้วยบรรทัดว่างระหว่างรายการ
const providerLabel = (key) => (key ? key.charAt(0) + key.slice(1).toLowerCase() : '');

export function NoteBox({ value, authorName, onSave, uiLang = 'en', projectId }) {
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  // ปุ่มไมค์ในช่องเพิ่มบันทึก — พูดแล้วต่อท้ายข้อความ (ใช้ระบบรู้จำเสียงของ Chrome/Edge)
  const { supported: micSupported, listening, error: micError, start: startMic, stop: stopMic } =
    useSpeechDictation({ getText: () => draft, onText: setDraft });
  const [listOpen, setListOpen] = useState(false);
  // เรียง/กรองในหน้า "ดูทั้งหมด": sort = { key: 'date'|'text'|'by'|null, dir: 'asc'|'desc' }, authorFilter = ชื่อผู้บันทึก
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [authorFilter, setAuthorFilter] = useState('');
  // AI สรุปเรื่องราว — เรียกครั้งเดียวตอนเปิดหน้านี้ (เปลี่ยนเมื่อมีบันทึกใหม่) กดสรุปใหม่เองได้
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  // ข้อมูลผู้สรุป (provider/model/เวลา/แหล่งที่มา) + ผลที่เก็บไว้เก่ากว่าบันทึกปัจจุบัน (stale)
  const [aiMeta, setAiMeta] = useState(null);
  // AI ที่ user เลือกเอง ('' = อัตโนมัติ) จำไว้ในเครื่อง — ใช้ AiProviderPicker (โมดูลใช้ซ้ำ) ตรงหัวกรอบสรุป
  const [aiProvider, setAiProvider] = useState(() => loadSavedAiProvider('notes_summary_ai_provider'));

  // แยก log เป็นรายการ: บรรทัดที่ขึ้นต้นด้วยวันที่ "D-M-YY " = รายการใหม่, บรรทัดอื่นต่อท้ายรายการก่อนหน้า
  // แล้วจัดกลุ่มตามวัน (บันทึกใหม่อยู่บนสุดอยู่แล้ว) — แต่ละรายการแยกข้อความกับชื่อผู้บันทึก (" — ชื่อ" ท้ายสุด)
  const noteGroups = (() => {
    const entries = [];
    String(value || '')
      .split('\n')
      .forEach((line) => {
        const m = line.match(/^(\d{1,2}-\d{1,2}-\d{2})\s+(.*)$/);
        if (m) entries.push({ date: m[1], raw: m[2] });
        else if (line.trim() && entries.length) entries[entries.length - 1].raw += `\n${line}`;
      });
    const groups = [];
    entries.forEach(({ date, raw }) => {
      const cut = raw.lastIndexOf(' — ');
      const item = cut >= 0
        ? { text: raw.slice(0, cut), by: raw.slice(cut + 3) }
        : { text: raw, by: '' };
      const last = groups[groups.length - 1];
      if (last && last.date === date) last.items.push(item);
      else groups.push({ date, items: [item] });
    });
    return groups;
  })();

  const fetchSummary = async (force = false, provider = aiProvider) => {
    if (!projectId || !value) return;
    setAiLoading(true);
    setAiError('');
    try {
      const data = await dispatch(actions.projectSummarizeNotes(projectId, force, provider));
      setAiSummary(data?.summary || '');
      setAiMeta(data || null);
      if (data?.stale && data?.error) setAiError(data.error);
    } catch (error) {
      setAiSummary('');
      setAiMeta(null);
      setAiError(error?.response?.data?.message || error?.message || U('สรุปไม่สำเร็จ', 'Summary failed'));
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (!listOpen) return;
    setSort({ key: null, dir: 'asc' });
    setAuthorFilter('');
    fetchSummary(false);
    // เปิดหน้านี้: ใช้ผลที่เก็บไว้ถ้าบันทึกยังไม่เปลี่ยน (ไม่เรียก AI) ไม่งั้นสรุปใหม่
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOpen]);

  // แถวทั้งหมดแบบแบน (คงลำดับเดิม = ใหม่สุดไว้บน) แล้วกรองตามผู้บันทึก/เรียงตามคอลัมน์ที่เลือก
  const allRows = noteGroups.flatMap((g) => g.items.map((it) => ({ date: g.date, ...it })))
    .map((r, i) => ({ ...r, order: i }));
  const dateValue = (d) => {
    const [dd, mm, yy] = d.split('-').map(Number);
    return new Date(2000 + yy, mm - 1, dd).getTime();
  };
  const shownRows = allRows
    .filter((r) => !authorFilter || r.by === authorFilter)
    .sort((a, b) => {
      if (!sort.key) return a.order - b.order;
      let diff = 0;
      if (sort.key === 'date') diff = dateValue(a.date) - dateValue(b.date);
      else if (sort.key === 'by') diff = (a.by || '').localeCompare(b.by || '', 'th');
      else diff = a.text.localeCompare(b.text, 'th');
      if (diff === 0) return a.order - b.order;
      return sort.dir === 'asc' ? diff : -diff;
    });
  // คลิกหัวคอลัมน์ (หรือชื่อผู้บันทึก): ครั้งแรก = น้อย→มาก, ซ้ำ = มาก→น้อย, ซ้ำอีก = กลับลำดับเดิม
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };
  const sortMark = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // เอาผลสรุปไปเป็นบันทึกใหม่อัตโนมัติ — ใช้เฉพาะย่อหน้า "สรุป:" (ไม่เอาทั้งก้อน) บีบให้เหลือบรรทัดเดียวตามรูปแบบ log
  const handleAddSummaryAsNote = () => {
    if (!aiSummary) return;
    const m = aiSummary.match(/สรุป\s*:?\s*([\s\S]*?)(?:\n\s*\n|\nงานค้าง|$)/);
    const body = (m?.[1] || aiSummary).replace(/\s*\n\s*/g, ' ').trim().slice(0, 400);
    if (!body) return;
    const who = aiMeta?.provider ? `AI ${providerLabel(aiMeta.provider)}` : 'AI';
    const entry = `${dayjs().format('D-M-YY')} [AI สรุป] ${body} — ${who}`;
    onSave(value ? `${entry}\n${value}` : entry);
  };

  const openModal = () => {
    setDraft('');
    setModalOpen(true);
  };

  const handleSave = () => {
    const text = draft.trim();
    if (!text) return;
    const dateStr = dayjs().format('D-M-YY');
    const entry = `${dateStr} ${text} — ${authorName || 'ไม่ระบุ'}`;
    const next = value ? `${entry}\n${value}` : entry;
    stopMic();
    onSave(next);
    setModalOpen(false);
  };

  return (
    // ไม่ตั้ง width เอง (เหมือนกล่องป้ายสรุปกำไรคาดการณ์ row 22 ด้านบนที่ก็ไม่ตั้ง width เหมือนกัน) —
    // ปล่อยให้ stretch เต็มคอลัมน์ parent ตาม align-items:stretch ปกติ ขอบขวาเลยชนกับป้ายสรุปกำไร
    // พอดีเสมอไม่ว่าคอลัมน์ parent จะกว้างแคบแค่ไหน (ตั้ง width เป็น px ตายตัวจะทำให้ขอบไม่ตรงกัน
    // ตอนคอลัมน์ parent กว้าง/แคบกว่าตัวเลขที่ตั้งไว้)
    <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5, flexShrink: 0 }}
      >
        <Chip
          label={U('หมายเหตุ', 'Notes')}
          size="small"
          sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }}
        />
        <Stack direction="row" spacing={0.5}>
          {projectId && <AiQuestionsButton projectId={projectId} />}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen(true)}
            sx={{ fontSize: '0.7rem', py: 0.2, minWidth: 0 }}
          >
            {U('ดูทั้งหมด', 'View all')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={openModal}
            sx={{ fontSize: '0.7rem', py: 0.2, minWidth: 0 }}
          >
            + {U('เพิ่มบันทึก', 'Add note')}
          </Button>
        </Stack>
      </Stack>
      <Box
        sx={{
          border: EDITABLE_BORDER,
          borderRadius: 1.5,
          // ล็อกความสูงที่ ~13 บรรทัด (line-height 1.5 × 0.8rem × 13 + padding บน/ล่าง 1.5×2×8px) —
          // เกินนั้นเลื่อนอ่านข้างในกล่อง ไม่ขยายกล่องตามจำนวนบันทึกอีกต่อไป
          height: 'calc(13 * 1.5 * 0.8rem + 24px)',
          flexShrink: 0,
          boxSizing: 'border-box',
          overflowY: 'auto',
          p: 1.5,
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          color: value ? 'text.primary' : 'text.disabled',
        }}
      >
        {value || U('ยังไม่มีบันทึก', 'No notes')}
      </Box>

      <Dialog open={listOpen} onClose={() => setListOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {U('บันทึกทั้งหมด', 'All notes')}
              {allRows.length > 0 &&
                ` (${shownRows.length}${authorFilter ? `/${allRows.length}` : ''} ${U('รายการ', 'items')})`}
            </Typography>
            <Button size="small" variant="contained" onClick={openModal} sx={{ fontSize: '0.72rem', py: 0.2 }}>
              + {U('เพิ่มบันทึก', 'Add note')}
            </Button>
          </Stack>

          {/* AI สรุปเรื่องราว */}
          {allRows.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: '#f5f3ff',
                border: '1px solid #ddd6fe',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#5b21b6' }}>
                  ✨ {U('AI สรุปเรื่องราว', 'AI story summary')}
                  {aiMeta?.provider && !aiLoading
                    ? ` (${providerLabel(aiMeta.provider)} · ${aiPlanLabel(aiMeta.plan, uiLang)}${aiMeta.model ? ` · ${aiMeta.model}` : ''})`
                    : ''}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <AiProviderPicker
                    value={aiProvider}
                    uiLang={uiLang}
                    storageKey="notes_summary_ai_provider"
                    onChange={(v) => {
                      setAiProvider(v);
                      fetchSummary(false, v);
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => fetchSummary(true)}
                    disabled={aiLoading}
                    sx={{ fontSize: '0.68rem', minWidth: 0, py: 0 }}
                  >
                    {U('สรุปใหม่', 'Refresh')}
                  </Button>
                </Stack>
              </Stack>
              {aiLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    {U('AI กำลังสรุป...', 'AI is summarizing...')}
                  </Typography>
                </Stack>
              ) : aiError ? (
                <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                  {aiError}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {/* AI บางตัวตอบเป็น markdown (** ตัวหนา **) — หน้านี้แสดงเป็นข้อความธรรมดา เลยตัดเครื่องหมายออก */}
                  {aiSummary ? aiSummary.replace(/\*\*/g, '') : '-'}
                </Typography>
              )}
              {aiMeta?.provider && !aiLoading && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#5b21b6' }}>
                  {U('สรุปโดย', 'Summarized by')} {providerLabel(aiMeta.provider)} ({aiPlanLabel(aiMeta.plan, uiLang)})
                  {aiMeta.model ? ` · ${aiMeta.model}` : ''}
                  {aiMeta.generated_at ? ` · ${dayjs(aiMeta.generated_at).format('D-M-YY HH:mm')}` : ''}
                  {aiMeta.source ? ` · ${aiMeta.source === 'auto' ? U('อัตโนมัติรอบกลางคืน', 'nightly auto') : U('กดสรุปเอง', 'manual')}` : ''}
                  {aiMeta.stale ? ` · ${U('ผลเก่า (สรุปใหม่ไม่สำเร็จ)', 'stale (refresh failed)')}` : ''}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.disabled">
                  {U('สรุปโดย AI จากบันทึกด้านล่าง ตรวจสอบกับบันทึกจริงก่อนใช้', 'AI-generated from the notes below — verify before use')}
                </Typography>
                <Button
                  size="small"
                  onClick={handleAddSummaryAsNote}
                  disabled={aiLoading || !aiSummary}
                  sx={{ fontSize: '0.68rem', minWidth: 0, py: 0 }}
                >
                  + {U('เพิ่มเป็นบันทึก', 'Add as note')}
                </Button>
              </Stack>
            </Box>
          )}

          {authorFilter && (
            <Chip
              label={`${U('ผู้บันทึก', 'By')}: ${authorFilter}`}
              size="small"
              onDelete={() => setAuthorFilter('')}
              sx={{ mb: 1, bgcolor: '#7c3aed', color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff' } }}
            />
          )}

          {allRows.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              {U('ยังไม่มีบันทึก', 'No notes')}
            </Typography>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    ['date', U('วันที่', 'Date')],
                    ['text', U('รายละเอียด', 'Details')],
                    ['by', U('ผู้บันทึก', 'By')],
                  ].map(([key, label]) => (
                    <TableCell
                      key={key}
                      onClick={() => toggleSort(key)}
                      sx={{
                        fontWeight: 700,
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                      {sortMark(key)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {shownRows.map((r) => (
                  <TableRow key={r.order} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: '0.8rem', width: 78 }}>
                      {r.date}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>{r.text}</TableCell>
                    <Tooltip title={U('คลิก = เรียงตามผู้บันทึก · ดับเบิลคลิก = ดูเฉพาะคนนี้', 'Click = sort · Double-click = show only this person')} placement="left">
                      <TableCell
                        onClick={() => toggleSort('by')}
                        onDoubleClick={() => {
                          if (r.by) setAuthorFilter((prev) => (prev === r.by ? '' : r.by));
                        }}
                        sx={{
                          whiteSpace: 'nowrap',
                          verticalAlign: 'top',
                          fontSize: '0.8rem',
                          color: authorFilter && r.by === authorFilter ? '#7c3aed' : 'text.secondary',
                          fontWeight: authorFilter && r.by === authorFilter ? 700 : 400,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {r.by || '-'}
                      </TableCell>
                    </Tooltip>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setListOpen(false)}>{U('ปิด', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalOpen}
        onClose={() => {
          stopMic();
          setModalOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {U('เพิ่มบันทึกใหม่', 'Add a new note')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder={listening
              ? U('กำลังฟัง... พูดได้เลย', 'Listening... speak now')
              : U('พิมพ์หรือกดไมค์เพื่อพูดข้อความบันทึก...', 'Type or tap the mic to dictate...')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <Tooltip
                    title={
                      !micSupported
                        ? U('เบราว์เซอร์นี้ไม่รองรับ ใช้ Google Chrome หรือ Edge', 'Not supported — use Chrome or Edge')
                        : listening
                        ? U('หยุดฟัง', 'Stop')
                        : U('กดแล้วพูดได้เลย', 'Tap and speak')
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={listening ? stopMic : startMic}
                        disabled={!micSupported}
                        sx={{
                          color: listening ? '#dc2626' : 'primary.main',
                          ...(listening && {
                            animation: 'noteMicPulse 1.2s ease-in-out infinite',
                            '@keyframes noteMicPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
                          }),
                        }}
                      >
                        {listening ? <StopCircleIcon /> : <MicIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          {micError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#b91c1c' }}>
              {micError}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {U('บันทึกในชื่อ', 'Saved as')} {authorName || U('ไม่ระบุ', 'Unknown')} · {dayjs().format('D-M-YY')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { stopMic(); setModalOpen(false); }}>{U('ยกเลิก', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!draft.trim()}
          >
            {U('บันทึก', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
